import React, { createContext, useContext, useState, ReactNode } from "react";
import { SimpleOrder } from "../interfaces/simpleOrder";
import { Order } from "../interfaces/order";

interface AllOrdersContextType {
  allOrders: SimpleOrder[];
  setAllOrders: React.Dispatch<React.SetStateAction<SimpleOrder[]>>;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  searchApplied: string;
  setSearchApplied: React.Dispatch<React.SetStateAction<string>>;
  startDate: string | null;
  setStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  endDate: string | null;
  setEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  orderType: string | null;
  setOrderType: React.Dispatch<React.SetStateAction<string | null>>;
  searchType: string | null;
  setSearchType: React.Dispatch<React.SetStateAction<string | null>>;
  appliedStartDate: string | null;
  setAppliedStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  appliedEndDate: string | null;
  setAppliedEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  appliedOrderType: string | null;
  setAppliedOrderType: React.Dispatch<React.SetStateAction<string | null>>;
  appliedSearchType: string | null;
  setAppliedSearchType: React.Dispatch<React.SetStateAction<string | null>>;
  hasMore: boolean;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
  updateOrderInAllList: (updatedOrder: Order) => void;
}

const AllOrdersContext = createContext<AllOrdersContextType | undefined>(
  undefined,
);

export const AllOrdersProvider = ({ children }: { children: ReactNode }) => {
  const [allOrders, setAllOrders] = useState<SimpleOrder[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<string | null>("Candidate");
  const [appliedStartDate, setAppliedStartDate] = useState<string | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<string | null>(null);
  const [appliedOrderType, setAppliedOrderType] = useState<string | null>(null);
  const [appliedSearchType, setAppliedSearchType] = useState<string | null>(
    "Candidate",
  );
  const [hasMore, setHasMore] = useState(true);

  const updateOrderInAllList = (order: Order) => {
    setAllOrders((prev) =>
      prev.map((o) =>
        o.orderID === order.orderID
          ? {
              ...o,
              candidateFirstName: order.candidateFirstName,
              candidateLastName: order.candidateLastName,
              candidateMI: order.candidateMI,
              designation: order.designation ?? undefined,
              orderStatusID: order.orderStatusID,
              lastUpdatedDate: new Date(),
            }
          : o,
      ),
    );
  };

  return (
    <AllOrdersContext.Provider
      value={{
        allOrders,
        setAllOrders,
        pageNumber,
        setPageNumber,
        searchTerm,
        setSearchTerm,
        searchApplied,
        setSearchApplied,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        orderType,
        setOrderType,
        searchType,
        setSearchType,
        appliedStartDate,
        setAppliedStartDate,
        appliedEndDate,
        setAppliedEndDate,
        appliedOrderType,
        setAppliedOrderType,
        appliedSearchType,
        setAppliedSearchType,
        hasMore,
        setHasMore,
        updateOrderInAllList,
      }}
    >
      {children}
    </AllOrdersContext.Provider>
  );
};

export const useAllOrdersContext = () => {
  const context = useContext(AllOrdersContext);
  if (!context)
    throw new Error("useAllOrdersContext must be used within OrderProvider");
  return context;
};
